import { boolean, ref, string, number, object, array } from 'yup';
import valid from 'card-validator';
import moment from 'moment';

interface SchemeOptions {
  noslash?: boolean;
  required?: boolean;
  allowEmpty?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  valid?: boolean;
  minMessage?: string;
  message?: string;
  format?: boolean;
}

export const EMAIL_REGEXP = /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
export const PROJECT_NAME_REGEXP = /^[a-zA-Z0-9Ѐ-ӿ].{0,125}$/;
const PROJECT_NAME_MESSAGE = 'Must start with a letter or number, max 126 characters.';
const URL_REGEXP =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
const ZENDESK_ARTICLES_URL = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\/.*?\/articles\/\d+.*$/;

export const emailScheme = (opt?: SchemeOptions) => {
  const rule = string()
    .email('Invalid format')
    .required('Required')
    .matches(EMAIL_REGEXP, 'Invalid format')
    .strict();

  if (opt?.required) {
    return rule.required();
  }

  if (opt?.allowEmpty) {
    return string();
  }

  return rule;
};

export const booleanScheme = (opt?: SchemeOptions) => {
  const rule = boolean();
  if (opt?.required) return rule.required();

  return rule;
};

export const booleanOneOfScheme = (opt?: SchemeOptions) => {
  const rule = boolean();
  if (opt?.required) return rule.oneOf([true], 'Required');
  return rule;
};

export const passwordScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!value : true;
    })
    .when({
      is: (value: string) => value.length > 0,
      then: () =>
        string()
          .min(opt?.min || 8, `${opt?.minMessage}`)
          .max(opt?.max || 50)
          .label('Password'),
    });
};

export const passwordConfirmScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!value : true;
    })
    .when('password', (pass: string[], schema) => {
      if (pass[0]) {
        return passwordScheme({ required: true });
      }
      return schema;
    })
    .when({
      is: (value: string) => value.length > 0,
      then: () =>
        string()
          .min(opt?.min || 8)
          .max(opt?.max || 50)
          .oneOf([ref('password')], 'Your passwords do not match.')
          .label('Password confirm'),
    });
};

export const stringScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      const val = value || '';
      return opt?.required ? !!val.trim() : true;
    })
    .test('noslash', '"/" is not allowed', (value) => {
      const val = value || '';

      return opt?.noslash ? !val.includes('/') : true;
    })
    .min(opt?.min || 0, `${opt?.minMessage}`);
};

export const projectNameScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      const val = value || '';
      return opt?.required ? !!val.trim() : true;
    })
    .test('format', PROJECT_NAME_MESSAGE, (value) => {
      const val = value || '';
      return val ? PROJECT_NAME_REGEXP.test(val) : true;
    });
};

export const numberScheme = (opt?: SchemeOptions) => {
  return number().test('required', 'Required', (value) => {
    return opt?.required ? typeof value === 'number' : true;
  });
};

export const creditCardNumberScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!(value ?? '').trim() : true;
    })
    .test(
      'test-number',
      'Credit Card number is invalid',
      (value) => valid.number(value ?? '').isValid,
    );
};

export const expirationCreditCardScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!(value ?? '').trim() : true;
    })
    .test(
      'test-expiration',
      'Credit Card Expiration date is invalid',
      (value) => valid.expirationDate(value ?? '').isValid,
    );
};

export const CVVCreditCardScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!(value ?? '').trim() : true;
    })
    .test('test-cvv', 'CVC format is invalid', (value) => valid.cvv(value ?? '').isValid);
};

export const holderCreditCardScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!(value ?? '').trim() : true;
    })
    .test(
      'test-holder',
      'Card Holder is invalid',
      (value) => valid.cardholderName(value ?? '').isValid,
    );
};

export const phoneNumberScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!value : true;
    })
    .when({
      is: (value: string | undefined) => value?.length,
      then: (rule) => rule.min(opt?.min || 5, `Minimum ${opt?.min || 5} digits`),
    })
    .when({
      is: (value: string | undefined) => value?.length,
      then: (rule) => rule.max(opt?.max || 15, `Maximum ${opt?.max || 15} digits`),
    });
};

export const urlScheme = (opt?: SchemeOptions) => {
  const rule = string()
    .required('Required')
    .matches(URL_REGEXP, 'Invalid Format. Should be https://**.**')
    .strict();

  if (opt?.required) return rule.required();

  return rule;
};

export const urlZendeskArticlesScheme = (opt?: SchemeOptions) => {
  const rule = string()
    .required('Required')
    .matches(
      ZENDESK_ARTICLES_URL,
      'Enter a valid article link that includes https://**/**/articles/**',
    )
    .strict();

  if (opt?.required) return rule.required();

  return rule;
};

export const languageScheme = (opt?: SchemeOptions) => {
  const rule = object().shape({
    key: string(),
    value: string(),
  });

  if (opt?.required) return rule.required();

  return rule;
};

export const excludedLanguagesScheme = (opt?: SchemeOptions) => {
  const rule = array().of(
    object().shape({
      key: string(),
      value: string(),
    }),
  );

  if (opt?.required) return rule.min(1, 'Excluded Languages is required');

  return rule;
};

export const websiteLinksScheme = (opt?: SchemeOptions) => {
  const rule = array().of(
    object().shape({
      pf_page_name: string(),
      pf_page_description: string(),
      pf_page_url: string(),
    }),
  );
  if (opt?.required) return rule.min(1, 'Website Links is required');

  return rule;
};

export const arrayOfStringsScheme = (opt?: SchemeOptions) => {
  const rule = array().of(string());

  if (opt?.required) return rule.min(1, opt.message || '');

  return rule;
};

export const arrayOfCtaScheme = (opt?: SchemeOptions) => {
  const rule = array().of(
    object().shape({
      pf_cta_value: string().required('CTA is required'),
      pf_cta_condition: string().required('Condition is required'),
    }),
  );
  if (opt?.required) return rule.min(1, 'CTA is required');

  return rule;
};

export const momentScheme = (opt?: SchemeOptions) => {
  return string().test('valid', 'Invalid Date', (value) => {
    if (opt?.required) {
      const date = moment(value);

      return date.isValid();
    }

    return true;
  });
};

export const stringEmailScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value = '') => {
      return opt?.required ? !!value.trim() : true;
    })
    .test('format', 'Invalid Format', (value = '') => {
      if (opt?.format) {
        const arr = value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        return !arr.filter((email) => {
          return !EMAIL_REGEXP.test(email);
        }).length;
      }

      return true;
    });
};
